import React, { useState, useEffect, useRef } from "react";
import DefaultFooter from "../components/default-footer";
import Header1 from "../components/default-header";
import { useNavigate } from "react-router-dom";
import { useCountries } from "../hooks/useCountries";
import { useTranslation } from "react-i18next";
import { useSeo } from "../components/useSeo";
import { breadcrumbJsonLd } from "../components/seoData";
import Header from "../components/header";

type DropdownType = 'destination' | 'nationality' | 'livingIn' | null;

interface CountryOption {
  id: string | number;
  name: string;
}

// ── Reusable searchable country dropdown ─────────────────────────────────
interface CountryDropdownFieldProps {
  fieldKey: Exclude<DropdownType, null>;
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onSelect: (id: string) => void;
  countries: CountryOption[];
  activeDropdown: DropdownType;
  setActiveDropdown: (d: DropdownType) => void;
}

function CountryDropdownField({
  fieldKey,
  icon,
  label,
  placeholder,
  value,
  onSelect,
  countries,
  activeDropdown,
  setActiveDropdown,
}: CountryDropdownFieldProps) {
  const isOpen = activeDropdown === fieldKey;
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Réinitialise la recherche à chaque fermeture, et focus l'input à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      const t = setTimeout(() => searchInputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const filtered = query
    ? countries.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
    : countries;

  const selected = countries.find((c) => String(c.id) === String(value));

  return (
    <div className={`visa-field ${isOpen ? "is-active" : ""}`}>
      <div className="visa-field-icon-wrap">{icon}</div>
      <div className="visa-field-content">
        <label className="visa-field-label">{label}</label>
        <div className="custom-dropdown-wrap">
          <button
            type="button"
            className={`custom-dropdown-trigger ${!value ? "placeholder" : ""}`}
            onClick={() => setActiveDropdown(isOpen ? null : fieldKey)}
          >
            <span>{selected ? selected.name : placeholder}</span>
            <span className={`custom-dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
          </button>

          {isOpen && (
            <div className="custom-dropdown-menu">
              <div className="custom-dropdown-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search country…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                {query && (
                  <button
                    type="button"
                    className="custom-dropdown-search-clear"
                    onClick={(e) => { e.stopPropagation(); setQuery(""); }}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="custom-dropdown-options">
                {filtered.length === 0 ? (
                  <div className="custom-dropdown-empty">No country matches "{query}"</div>
                ) : (
                  filtered.map((c) => (
                    <div
                      key={c.id}
                      className={`custom-dropdown-option ${String(c.id) === String(value) ? "selected" : ""}`}
                      onClick={() => {
                        onSelect(String(c.id));
                        setActiveDropdown(null);
                      }}
                    >
                      <span>{c.name}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const SearchVisa = () => {
  const navigate = useNavigate();
  const { countries, loading } = useCountries();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

    const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "Search Tourist Visas | SilverJarz",
    description: "Search tourist visas by destination and nationality — find the right visa for your trip from Oman with SilverJarz.",
    canonical: `${origin}/search-visa`,
    jsonLd: breadcrumbJsonLd([
      { name: "Home", url: `${origin}/` },
      { name: "Visas", url: `${origin}/search-visa` },
    ]),
  });

  const [searchParams, setSearchParams] = useState({
    destination_country_id: "",
    nationality_id: "",
    living_in_id: "",
  });

  const [activeDropdown, setActiveDropdown] = useState<DropdownType>(null);
  const searchCardRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchCardRef.current && !searchCardRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setSearchParams((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch_old = (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchParams.destination_country_id || !searchParams.nationality_id) {
      alert("Please select destination and nationality country.");
      return;
    }

    navigate("/visa-results", {
      state: {
        destination_country_id: searchParams.destination_country_id,
        nationality_id: searchParams.nationality_id,
      },
    });
  };

  const handleSearch = (e: React.FormEvent) => {
  e.preventDefault();

  if (!searchParams.destination_country_id || !searchParams.nationality_id) {
    alert("Please select destination and nationality country.");
    return;
  }

  // Récupérer le nom du pays sélectionné pour la nationalité
  const selectedNationality = countries.find(
    (c) => String(c.id) === String(searchParams.nationality_id)
  );

  navigate("/visa-results", {
    state: {
      destination_country_id: searchParams.destination_country_id,
      nationality_id: searchParams.nationality_id,
      nationality_name: selectedNationality?.name || "", // Ajouter le nom
    },
  });
};

  return (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
    
      :root {
        --primary: #294b5a;
        --primary-dark: #1a313d;
        --gold: #b89b41;
        --gold-light: #e8d99a;
        --cream: #faf8f4;
        --text-main: #1f2937;
        --text-muted: #4b5563;
        --border-color: #e5e7eb;
      }
    
      .visa-page-wrapper {
        font-family: 'DM Sans', sans-serif;
        background: var(--cream);
        color: var(--text-main);
        width: 100%;
        max-width: 100vw;
        overflow-x: hidden;
      }
    
      .visa-hero {
        position: relative;
        background: linear-gradient(145deg, #eef3f5 0%, #d5e2e7 100%);
        padding: 40px 0 40px;
        overflow: visible;
      }
    
      .visa-hero-bg-lines {
        position: absolute;
        inset: 0;
        background-image: radial-gradient(rgba(26, 49, 61, 0.12) 1px, transparent 1px);
        background-size: 32px 32px;
        pointer-events: none;
        opacity: 0.6;
      }
    
      .visa-hero-glow {
        position: absolute;
        top: -10%;
        right: 0;
        width: 500px;
        height: 500px;
        background: radial-gradient(circle, rgba(184, 155, 65, 0.2) 0%, rgba(0,0,0,0) 70%);
        pointer-events: none;
      }
    
      .visa-hero-content {
        position: relative;
        z-index: 2;
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 24px;
      }
    
      .visa-eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 1.5px;
        color: var(--primary-dark);
        margin-bottom: 20px;
        background: rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(8px);
        padding: 6px 16px;
        border-radius: 99px;
        border: 1px solid rgba(184, 155, 65, 0.4);
        box-shadow: 0 2px 10px rgba(26, 49, 61, 0.04);
      }
    
      .visa-hero-title {
        font-family: 'Cormorant Garamond', serif;
        font-size: clamp(2.8rem, 5vw, 4.2rem);
        font-weight: 400;
        color: var(--primary-dark);
        line-height: 1.1;
        margin: 0 0 16px;
        letter-spacing: -0.5px;
      }
    
      .visa-hero-title em {
        font-style: italic;
        color: var(--gold);
        font-weight: 500;
      }
    
      .visa-hero-subtitle {
        font-size: 16px;
        color: #3d5866;
        font-weight: 400;
        margin-bottom: 40px;
        max-width: 540px;
        line-height: 1.6;
      }
    
      .visa-search-container {
        position: relative;
        margin-top: 10px;
        z-index: 10;
      }
    
      .visa-trust-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: #ffffff;
        color: var(--primary-dark);
        font-size: 12px;
        font-weight: 600;
        padding: 6px 14px;
        border-radius: 12px 12px 0 0;
        box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.03);
        margin-left: 16px;
        border: 1px solid rgba(26, 49, 61, 0.1);
        border-bottom: none;
      }
    
      .visa-trust-badge-icon {
        width: 16px;
        height: 16px;
        background: var(--gold);
        color: #ffffff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 700;
      }
    
      .visa-search-card {
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 20px 50px -10px rgba(26, 49, 61, 0.12);
        border: 1px solid rgba(26, 49, 61, 0.08);
        padding: 10px;
      }
    
      .visa-search-inner {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    
      .visa-field {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px 16px;
        border-radius: 12px;
        transition: all 0.2s ease;
        background: transparent;
        border: 1px solid transparent;
        position: relative;
      }
    
      .visa-field:hover {
        background: rgba(41, 75, 90, 0.03);
      }
    
      .visa-field.is-active {
        background: #ffffff;
        border-color: var(--gold);
        box-shadow: 0 0 0 4px rgba(184, 155, 65, 0.15);
      }
    
      .visa-field-divider {
        width: 1px;
        height: 40px;
        background: var(--border-color);
        flex-shrink: 0;
      }
    
      .visa-field-icon-wrap {
        flex-shrink: 0;
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: rgba(41, 75, 90, 0.06);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--primary);
        transition: all 0.2s ease;
      }
    
      .visa-field.is-active .visa-field-icon-wrap {
        background: var(--primary);
        color: var(--gold);
      }
    
      .visa-field-content {
        flex: 1;
        min-width: 0;
      }
    
      .visa-field-label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.5px;
        color: var(--text-muted);
        margin-bottom: 2px;
        display: block;
      }
    
      .custom-dropdown-wrap {
        position: relative;
        user-select: none;
      }
    
      .custom-dropdown-trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        background: transparent;
        border: none;
        font-family: 'DM Sans', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: var(--primary-dark);
        cursor: pointer;
        padding: 0;
        text-align: left;
      }
    
      .custom-dropdown-trigger.placeholder {
        color: var(--text-muted);
        font-weight: 400;
      }
    
      .custom-dropdown-arrow {
        color: var(--text-muted);
        display: flex;
        align-items: center;
        transition: transform 0.25s ease;
      }
    
      .custom-dropdown-arrow.open {
        transform: rotate(180deg);
        color: var(--primary);
      }
    
      .custom-dropdown-menu {
        position: absolute;
        top: calc(100% + 16px);
        left: -8px;
        right: -8px;
        background: #ffffff;
        border-radius: 14px;
        box-shadow: 0 16px 36px -8px rgba(26, 49, 61, 0.25);
        border: 1px solid rgba(184, 155, 65, 0.35);
        z-index: 100;
        padding: 6px;
        animation: dropdownFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      }

      /* ── Search box inside dropdown ── */
      .custom-dropdown-search {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        margin: 2px 2px 6px;
        background: var(--cream);
        border: 1px solid rgba(26, 49, 61, 0.1);
        border-radius: 9px;
        color: var(--text-muted);
      }
      .custom-dropdown-search svg { flex-shrink: 0; }
      .custom-dropdown-search input {
        flex: 1;
        border: none;
        background: transparent;
        outline: none;
        font-family: 'DM Sans', sans-serif;
        font-size: 13.5px;
        color: var(--text-main);
      }
      .custom-dropdown-search input::placeholder { color: #9aa5ad; }
      .custom-dropdown-search-clear {
        border: none;
        background: rgba(26, 49, 61, 0.08);
        color: var(--text-muted);
        width: 18px;
        height: 18px;
        border-radius: 50%;
        font-size: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
      }

      .custom-dropdown-options {
        max-height: 220px;
        overflow-y: auto;
      }
      .custom-dropdown-options::-webkit-scrollbar { width: 6px; }
      .custom-dropdown-options::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 8px; }
      .custom-dropdown-options::-webkit-scrollbar-thumb { background: var(--gold); border-radius: 8px; }

      .custom-dropdown-empty {
        padding: 16px 12px;
        text-align: center;
        font-size: 13px;
        color: var(--text-muted);
      }
    
      @keyframes dropdownFadeIn {
        from {
          opacity: 0;
          transform: translateY(-8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    
      .custom-dropdown-option {
        padding: 10px 14px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        color: var(--text-main);
        cursor: pointer;
        transition: all 0.15s ease;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    
      .custom-dropdown-option:hover {
        background: rgba(41, 75, 90, 0.06);
        color: var(--primary-dark);
      }
    
      .custom-dropdown-option.selected {
        background: var(--primary);
        color: #ffffff;
        font-weight: 600;
      }
    
      .visa-search-btn-wrap {
        flex-shrink: 0;
      }
    
      .visa-search-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
        color: #ffffff;
        border: none;
        border-radius: 12px;
        font-family: 'DM Sans', sans-serif;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        padding: 18px 36px;
        transition: all 0.25s ease;
        box-shadow: 0 8px 20px rgba(41, 75, 90, 0.2);
      }
    
      .visa-search-btn:hover {
        background: linear-gradient(135deg, #1f3a46 0%, #102028 100%);
        transform: translateY(-2px);
        box-shadow: 0 12px 24px rgba(41, 75, 90, 0.3);
      }
    
      .visa-search-btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }
    
      .visa-steps-section {
        padding: 60px 0;
        background: var(--cream);
        position: relative;
      }
    
      .visa-steps-header {
        text-align: center;
        max-width: 620px;
        margin: 0 auto 40px;
      }
    
      .visa-steps-eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 2px;
        color: var(--gold);
        margin-bottom: 12px;
      }
    
      .visa-steps-eyebrow::before,
      .visa-steps-eyebrow::after {
        content: '';
        width: 24px;
        height: 1px;
        background: var(--gold);
        opacity: 0.6;
      }
    
      .visa-steps-title {
        font-family: 'Cormorant Garamond', serif;
        font-size: clamp(2.2rem, 4vw, 3.2rem);
        font-weight: 400;
        color: var(--primary-dark);
        line-height: 1.2;
        margin: 0 0 12px;
      }
    
      .visa-steps-subtitle {
        font-size: 15px;
        color: var(--text-muted);
        font-weight: 400;
        line-height: 1.6;
      }
    
      .visa-steps-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 24px;
        position: relative;
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 24px;
      }
    
      .visa-steps-grid::before {
        content: '';
        position: absolute;
        top: 90px;
        left: 120px;
        right: 120px;
        height: 1px;
        background: linear-gradient(90deg, 
          rgba(184, 155, 65, 0.1) 0%, 
          rgba(184, 155, 65, 0.5) 50%, 
          rgba(184, 155, 65, 0.1) 100%
        );
        z-index: 1;
      }
    
      .visa-step-card {
        position: relative;
        z-index: 2;
        background: #ffffff;
        border-radius: 16px;
        padding: 32px 24px;
        border: 1px solid rgba(184, 155, 65, 0.2);
        transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
        display: flex;
        flex-direction: column;
        box-shadow: 0 10px 30px -10px rgba(41, 75, 90, 0.05);
      }
    
      .visa-step-card:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 40px -12px rgba(41, 75, 90, 0.12);
        border-color: var(--gold);
      }
    
      .visa-step-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 24px;
      }
    
      .visa-step-icon {
        width: 52px;
        height: 52px;
        border-radius: 14px;
        background: rgba(41, 75, 90, 0.04);
        color: var(--primary);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
      }
    
      .visa-step-card:hover .visa-step-icon {
        background: var(--primary);
        color: var(--gold);
      }
    
      .visa-step-number {
        font-family: 'Cormorant Garamond', serif;
        font-size: 28px;
        font-weight: 600;
        color: rgba(184, 155, 65, 0.8);
        line-height: 1;
      }
    
      .visa-step-card-title {
        font-size: 18px;
        font-weight: 600;
        color: var(--primary-dark);
        margin: 0 0 10px;
      }
    
      .visa-step-card-desc {
        font-size: 14px;
        color: var(--text-muted);
        line-height: 1.6;
        margin: 0;
        font-weight: 400;
      }
    
      @media (max-width: 992px) {
        .visa-search-inner {
          flex-direction: column;
          gap: 0;
        }
    
        .visa-field {
          width: 100%;
          padding: 16px;
        }
    
        .visa-field-divider {
          width: 100%;
          height: 1px;
          margin: 4px 0;
        }
    
        .visa-search-btn-wrap {
          width: 100%;
          margin-top: 8px;
        }
    
        .visa-search-btn {
          width: 100%;
        }
    
        .custom-dropdown-menu {
          left: 0;
          right: 0;
        }

        .visa-steps-grid {
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }
        .visa-steps-grid::before {
          display: none;
        }
      }

      @media (max-width: 576px) {
        .visa-steps-grid {
          grid-template-columns: 1fr;
        }
        .visa-steps-section {
          padding: 40px 0;
        }
      }
    `}</style>
    
    <div className="visa-page-wrapper">
  <div className="header-margin"></div>
  <Header />

  <section className="visa-hero">
    <div className="visa-hero-bg-lines" />
    <div className="visa-hero-glow" />

    <div className="visa-hero-content">
      <span className="visa-eyebrow">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
        </svg>
        {t("visaEyebrow")}
      </span>

      <h1 className="visa-hero-title">
        {t("visaHeroTitle1")}<br />
        {t("visaHeroTitle2")} <em>{t("visaHeroTitleWord")}</em>
      </h1>
      <p className="visa-hero-subtitle">
        {t("visaHeroSubtitle")}
      </p>

      <div className="visa-search-container" ref={searchCardRef}>
        <div className="visa-trust-badge">
          <span className="visa-trust-badge-icon">✓</span>
          {t("visaTrustBadge")}
        </div>

        <form onSubmit={handleSearch} className="visa-search-card">
          <div className="visa-search-inner">

            <CountryDropdownField
              fieldKey="destination"
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>}
              label={t("visaDestinationLabel")}
              placeholder={t("visaDestinationPlaceholder")}
              value={searchParams.destination_country_id}
              onSelect={(id) => handleInputChange("destination_country_id", id)}
              countries={countries}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />

            <div className="visa-field-divider" />

            <CountryDropdownField
              fieldKey="nationality"
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 7h10M7 12h10M7 17h6"/></svg>}
              label={t("visaNationalityLabel")}
              placeholder={t("visaNationalityPlaceholder")}
              value={searchParams.nationality_id}
              onSelect={(id) => handleInputChange("nationality_id", id)}
              countries={countries}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />

            <div className="visa-field-divider" />

            <CountryDropdownField
              fieldKey="livingIn"
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>}
              label={t("visaLivingInLabel")}
              placeholder={t("visaLivingInPlaceholder")}
              value={searchParams.living_in_id}
              onSelect={(id) => handleInputChange("living_in_id", id)}
              countries={countries}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />

            <div className="visa-search-btn-wrap">
              <button type="submit" className="visa-search-btn" disabled={loading}>
                {loading ? t("visaLoading") : t("visaFindBtn")}
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>
  </section>

  <section className="visa-steps-section">
    <div className="visa-steps-header">
      <span className="visa-steps-eyebrow">{t("visaStepsEyebrow")}</span>
      <h2 className="visa-steps-title">{t("visaStepsTitle")}</h2>
      <p className="visa-steps-subtitle">
        {t("visaStepsSubtitle")}
      </p>
    </div>

    <div className="visa-steps-grid">
      <div className="visa-step-card">
        <div className="visa-step-top">
          <div className="visa-step-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <span className="visa-step-number">01</span>
        </div>
        <h3 className="visa-step-card-title">{t("visaStep1Title")}</h3>
        <p className="visa-step-card-desc">{t("visaStep1Desc")}</p>
      </div>

      <div className="visa-step-card">
        <div className="visa-step-top">
          <div className="visa-step-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
          </div>
          <span className="visa-step-number">02</span>
        </div>
        <h3 className="visa-step-card-title">{t("visaStep2Title")}</h3>
        <p className="visa-step-card-desc">{t("visaStep2Desc")}</p>
      </div>

      <div className="visa-step-card">
        <div className="visa-step-top">
          <div className="visa-step-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
          </div>
          <span className="visa-step-number">03</span>
        </div>
        <h3 className="visa-step-card-title">{t("visaStep3Title")}</h3>
        <p className="visa-step-card-desc">{t("visaStep3Desc")}</p>
      </div>

      <div className="visa-step-card">
        <div className="visa-step-top">
          <div className="visa-step-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <span className="visa-step-number">04</span>
        </div>
        <h3 className="visa-step-card-title">{t("visaStep4Title")}</h3>
        <p className="visa-step-card-desc">{t("visaStep4Desc")}</p>
      </div>
    </div>
  </section>

  <DefaultFooter />
</div>
  </>
  );
};

export default SearchVisa;